/**
 * Carrier profili -> Trucks (2026-10-25): truck'lar ro'yxati. Ko'rish -- hamma xodim; qo'shish, tahrirlash, holat, arxiv --
 * "carriers_edit" (owner, filial menejeri). Sug'urta / inspeksiya muddati: o'tgan -- qizil, yaqin -- sariq.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";
import { copyText } from "@/lib/clipboard";
import { useArchiveTruck, useSaveTruck, useTrucks } from "@/features/carriers/api";
import { truckMenu } from "@/features/carriers/menu";
import { TRUCK_STATUSES, type ExpiryState, type Truck, type TruckDriverRef, type TruckStatus } from "@/features/carriers/types";
import { StatusPill } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { confirmDialog } from "@/components/ui/confirm";
import { ContextMenu } from "@/components/ui/context-menu";
import { EmptyState, ErrorState, PageLoading } from "@/components/ui/misc";
import { FilterChips, Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { TruckFormModal } from "./TruckFormModal";

type Chip = "all" | TruckStatus;

function Expiry({ date, state }: { date: string | null; state: ExpiryState | null }) {
  const { t } = useTranslation("carriers");
  if (!date) return <span className="text-faint">—</span>;
  return (
    <span className={cn("whitespace-nowrap", state === "expired" ? "font-semibold text-danger" : state === "soon" ? "font-semibold text-warning" : "text-ink-soft")}>
      {formatDate(date)}
      {state === "expired" && <span className="ml-1 text-[11px]">· {t("trucks.expired")}</span>}
    </span>
  );
}

function DriverName({ d }: { d: TruckDriverRef }) {
  return d.can_open ? (
    <Link to={`/drivers/${d.id}`} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
      {d.full_name}
    </Link>
  ) : (
    <span>{d.full_name}</span>
  );
}

export function TrucksTab({ carrierId, canEdit }: { carrierId: number; canEdit: boolean }) {
  const { t } = useTranslation("carriers");
  const { t: tc } = useTranslation();
  const { t: tm } = useTranslation("menu");
  const navigate = useNavigate();
  const [chip, setChip] = useState<Chip>("all");
  const [editing, setEditing] = useState<Truck | null>(null);
  const [adding, setAdding] = useState(false);
  const trucks = useTrucks({ carrier: carrierId, ordering: "unit_number" });
  const save = useSaveTruck();
  const archive = useArchiveTruck();

  const all = trucks.data?.results ?? [];
  const rows = chip === "all" ? all : all.filter((tr) => tr.status === chip);
  const count = (s: TruckStatus) => all.filter((tr) => tr.status === s).length;
  const expiring = all.filter((tr) => tr.insurance_state === "expired" || tr.insurance_state === "soon" || tr.inspection_state === "expired" || tr.inspection_state === "soon").length;

  function setStatus(tr: Truck, status: TruckStatus) {
    save.mutate(
      { id: tr.id, data: { status, version: tr.version } },
      {
        onSuccess: () => toast.success(t("trucks.statusSet", { status: tc(`status.${status}`) })),
        onError: (e) => toast.error(e instanceof ApiError && e.isConflict ? tc("toast.conflictAnon") : e instanceof ApiError ? e.message : t("trucks.saveFailed")),
      },
    );
  }

  async function askArchive(tr: Truck) {
    const ok = await confirmDialog({ title: t("trucks.archiveTitle", { unit: tr.unit_number || `#${tr.id}` }), description: t("trucks.archiveDesc"), confirmLabel: tm("archive"), danger: true });
    if (!ok) return;
    archive.mutate(tr.id, { onSuccess: () => toast.success(t("trucks.archived")), onError: (e) => toast.error(e instanceof ApiError ? e.message : t("trucks.saveFailed")) });
  }

  const menu = (tr: Truck) =>
    truckMenu(tr, canEdit, { openDriver: (id) => navigate(`/drivers/${id}`), copy: (text) => void copyText(text), edit: () => setEditing(tr), setStatus: (s) => setStatus(tr, s), archive: () => void askArchive(tr) }, tm, tc);

  if (trucks.isPending) return <PageLoading />;
  if (trucks.isError) return <ErrorState message={trucks.error.message} onRetry={() => trucks.refetch()} />;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <FilterChips<Chip>
          items={[{ key: "all", label: t("trucks.chips.all"), count: all.length }, ...TRUCK_STATUSES.map((s) => ({ key: s, label: tc(`status.${s}`), count: count(s) }))]}
          value={chip}
          onChange={setChip}
        />
        <div className="flex items-center gap-3">
          {expiring > 0 && <span className="text-[12px] font-semibold text-warning">{t("trucks.expiringCount", { count: expiring })}</span>}
          {canEdit && <Button onClick={() => setAdding(true)}>{t("trucks.add")}</Button>}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title={all.length === 0 ? t("trucks.empty") : t("trucks.emptyFiltered")} description={all.length === 0 ? (canEdit ? t("trucks.emptyManager") : t("trucks.emptyViewer")) : undefined} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t("trucks.cols.truck")}</Th>
              <Th>{t("trucks.cols.trailer")}</Th>
              <Th>{t("trucks.cols.status")}</Th>
              <Th>{t("trucks.cols.driver")}</Th>
              <Th>{t("trucks.cols.location")}</Th>
              <Th>{t("trucks.cols.plate")}</Th>
              <Th>{t("trucks.cols.insurance")}</Th>
              <Th>{t("trucks.cols.inspection")}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((tr) => (
              <ContextMenu key={tr.id} items={() => menu(tr)}>
                <Tr clickable={canEdit} onClick={canEdit ? () => setEditing(tr) : undefined}>
                  <Td>
                    <div className="leading-tight">
                      <div className="font-semibold">{tr.unit_number ? `#${tr.unit_number}` : "—"}</div>
                      <div className="text-[11px] text-muted">
                        {[tr.year, tr.make, tr.model].filter(Boolean).join(" ") || "—"}
                        {tr.cab_type ? ` · ${t(`trucks.cab.${tr.cab_type}`)}` : ""}
                        {tr.transmission ? ` · ${t(`trucks.transmission.${tr.transmission}`)}` : ""}
                      </div>
                    </div>
                  </Td>
                  <Td>{tr.trailer_type ? `${t(`trucks.trailer.${tr.trailer_type}`)}${tr.trailer_length ? ` · ${tr.trailer_length}'` : ""}` : "—"}</Td>
                  <Td>
                    <StatusPill status={tr.status} />
                  </Td>
                  <Td>
                    <div className="leading-tight">
                      {tr.driver_ref ? <DriverName d={tr.driver_ref} /> : <span className="text-faint">{t("trucks.noDriver")}</span>}
                      {tr.co_driver_ref && (
                        <div className="text-[12px]">
                          + <DriverName d={tr.co_driver_ref} />
                        </div>
                      )}
                      <div className="text-[11px] text-muted">{t(`trucks.crew.${tr.crew}`)}</div>
                    </div>
                  </Td>
                  <Td>{[tr.city, tr.state].filter(Boolean).join(", ") || "—"}</Td>
                  <Td>
                    <div className="leading-tight">
                      <div>{tr.plate_number ? `${tr.plate_number}${tr.plate_state ? ` · ${tr.plate_state}` : ""}` : "—"}</div>
                      {tr.vin && <div className="font-mono text-[11px] text-muted">{tr.vin}</div>}
                    </div>
                  </Td>
                  <Td>
                    <Expiry date={tr.insurance_expires} state={tr.insurance_state} />
                  </Td>
                  <Td>
                    <Expiry date={tr.inspection_expires} state={tr.inspection_state} />
                  </Td>
                </Tr>
              </ContextMenu>
            ))}
          </tbody>
        </Table>
      )}

      <TruckFormModal open={adding} onOpenChange={setAdding} carrierId={carrierId} />
      <TruckFormModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} carrierId={carrierId} truck={editing} />
    </div>
  );
}
